import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Target, TrendingUp, TrendingDown, Minus, Download, Copy } from "lucide-react";
import RetentionQualityBlock from "./RetentionQualityBlock";

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "last30", label: "Last 30 Days" },
  { value: "custom", label: "Custom" },
  { value: "lifetime", label: "Lifetime" },
];
const fmtGBP = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n || 0);
const MomentumIcon = ({ value }) => value === "improving" ? <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> : value === "declining" ? <TrendingDown className="h-3.5 w-3.5 text-rose-600" /> : <Minus className="h-3.5 w-3.5 text-slate-400" />;
const momCls = (v) => v === "improving" ? "bg-emerald-100 text-emerald-700" : v === "declining" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600";

function Stat({ label, value, sub, testId }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={testId}>
      <div className="text-[11px] uppercase text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}

export default function CampaignIntelligence({ open, onOpenChange, campaignId, onAction }) {
  const [period, setPeriod] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [locationId, setLocationId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !campaignId) return;
    setLoading(true);
    const params = new URLSearchParams({ period });
    if (period === "custom" && customFrom && customTo) {
      params.set("custom_from", customFrom); params.set("custom_to", customTo);
    }
    if (locationId) params.set("location_id", locationId);
    apiClient.get(`/campaigns/${campaignId}/intelligence?${params.toString()}`)
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [open, campaignId, period, customFrom, customTo, locationId]);

  const o = data?.overview; const k = data?.kpis; const m = data?.productMix;
  const t = data?.trends; const loc = data?.locations; const fr = data?.fundraisers;
  const roi = data?.roi; const q = data?.quality;
  const canShowCustom = useMemo(() => period === "custom", [period]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [];
    rows.push(["Campaign", o?.name]);
    rows.push(["Period", data.period, data.periodStart, data.periodEnd]);
    rows.push([]);
    rows.push(["KPIs"]);
    Object.entries(k || {}).forEach(([key, val]) => rows.push([key, val]));
    rows.push([]);
    rows.push(["Product Mix"]);
    Object.entries(m || {}).forEach(([key, val]) => rows.push([key, val]));
    rows.push([]);
    rows.push(["Top Locations"]); rows.push(["Location","Signups","Revenue","Efficiency","Last staffed"]);
    (loc?.top || []).forEach(r => rows.push([r.locationName, r.signups, r.revenue, r.efficiency, r.lastStaffed || ""]));
    rows.push([]);
    rows.push(["Top Fundraisers"]); rows.push(["Fundraiser","Signups","Cancellations","Revenue","Shifts"]);
    (fr?.top || []).forEach(r => rows.push([r.fundraiserName, r.signups, r.cancellations, r.revenue, r.shifts]));
    const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const locLabel = locationId ? `_loc-${(data?.locations?.all || []).find(l => l.locationId === locationId)?.locationName?.replace(/\s+/g,"-") || locationId.slice(0,6)}` : "";
    a.href = url; a.download = `campaign_${o?.name || campaignId}_${data.period}${locLabel}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="campaign-intel-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-amber-600" />
            Campaign Intelligence
            {o && <span className="text-slate-900 font-semibold">· {o.name}</span>}
          </DialogTitle>
        </DialogHeader>

        {/* Filter */}
        <div className="flex flex-wrap items-center gap-2 py-2 border-b" data-testid="ci-filter-bar">
          {PERIODS.map(x => (
            <Button key={x.value} size="sm" variant={period === x.value ? "default" : "outline"}
                    onClick={() => setPeriod(x.value)} data-testid={`ci-period-${x.value}`}
                    className={period === x.value ? "bg-slate-900 text-white" : ""}>
              {x.label}
            </Button>
          ))}
          {canShowCustom && (
            <div className="flex items-center gap-1 ml-2" data-testid="ci-custom-range">
              <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="h-8 w-36" />
              <span className="text-slate-400 text-xs">to</span>
              <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="h-8 w-36" />
            </div>
          )}
          <div className="flex items-center gap-1 ml-auto" data-testid="ci-location-filter">
            <span className="text-xs text-slate-500">Location</span>
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs min-w-[180px]"
              data-testid="ci-location-select"
            >
              <option value="">All campaign locations</option>
              {(data?.locations?.all || []).map(l => <option key={l.locationId} value={l.locationId}>{l.locationName}</option>)}
            </select>
          </div>
        </div>

        {loading && <div className="py-8 text-center text-slate-500">Loading campaign intelligence...</div>}
        {!loading && data && (data.kpis?.totalSignups === 0 && data.kpis?.cancellations === 0) && (
          <div className="py-10 text-center text-slate-500 border border-dashed border-slate-200 rounded-lg mt-2" data-testid="ci-empty-state">
            No activity for selected location/date range.
          </div>
        )}
        {!loading && data && !(data.kpis?.totalSignups === 0 && data.kpis?.cancellations === 0) && (
          <div className="space-y-4 mt-2">
            {/* Overview */}
            <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="ci-overview">
              <div><div className="text-xs text-slate-500">Campaign</div><div className="font-semibold">{o?.name}</div></div>
              <div><div className="text-xs text-slate-500">Status</div><Badge className={o?.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>{o?.status}</Badge></div>
              <div><div className="text-xs text-slate-500">Created</div><div className="text-xs">{o?.createdAt ? new Date(o.createdAt).toLocaleDateString("en-GB") : "—"}</div></div>
              <div><div className="text-xs text-slate-500">Locations linked</div><div className="font-semibold">{o?.linkedLocationsCount ?? 0}</div></div>
              <div><div className="text-xs text-slate-500">Fundraisers</div><div className="font-semibold">{o?.assignedFundraisersCount ?? 0}</div></div>
              <div className="col-span-3"><div className="text-xs text-slate-500">Types enabled</div>
                <div className="flex gap-1 mt-1">
                  {o?.campaignTypes?.lottery && <Badge variant="outline">Lottery</Badge>}
                  {o?.campaignTypes?.regularGiving && <Badge variant="outline">Regular giving</Badge>}
                  {o?.campaignTypes?.oneOff && <Badge variant="outline">One-off</Badge>}
                  {!o?.campaignTypes?.lottery && !o?.campaignTypes?.regularGiving && !o?.campaignTypes?.oneOff && <span className="text-xs text-slate-400">None configured</span>}
                </div>
              </div>
            </CardContent></Card>

            {/* KPI */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ci-kpis">
              <Stat label="Total signups" value={k?.totalSignups ?? 0} />
              <Stat label="Net signups" value={k?.netSignups ?? 0} />
              <Stat label="Cancellations" value={k?.cancellations ?? 0} />
              <Stat label="Est. revenue" value={fmtGBP(k?.estRevenue)} />
              <Stat label="Avg / day" value={k?.avgPerDay ?? 0} />
              <Stat label="Avg / active location" value={k?.avgPerActiveLocation ?? 0} />
              <Stat label="Avg / fundraiser" value={k?.avgPerFundraiser ?? 0} />
            </div>

            {/* Retention Quality (scoped) */}
            {campaignId && (
              <RetentionQualityBlock endpoint={`/campaigns/${campaignId}/retention`} testId="ci-retention-quality" />
            )}

            {/* Product mix */}
            <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm" data-testid="ci-mix">
              <Stat label="Lottery singles" value={m?.lotterySingle ?? 0} />
              <Stat label="Lottery doubles" value={m?.lotteryDouble ?? 0} />
              <Stat label="Regular giving" value={m?.regularGiving ?? 0} />
              <Stat label="One-off" value={m?.oneOff ?? 0} />
              <Stat label="Avg value" value={fmtGBP(m?.avgValue)} />
            </CardContent></Card>

            {/* Trends */}
            <Card><CardContent className="p-4" data-testid="ci-trends">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase text-slate-500">Performance trend</div>
                <div className="flex gap-2">
                  <Badge className={momCls(t?.momentum7)}><MomentumIcon value={t?.momentum7} /> 7-day: {t?.momentum7}</Badge>
                  <Badge className={momCls(t?.momentum30)}><MomentumIcon value={t?.momentum30} /> 30-day: {t?.momentum30}</Badge>
                </div>
              </div>
              <div className="h-44">
                <ResponsiveContainer>
                  <LineChart data={t?.daily || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tickFormatter={d => d?.slice(5)} tick={{ fontSize: 10 }} interval={"preserveStartEnd"} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="signups" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent></Card>

            {/* Locations */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ci-locations">
              <Card><CardContent className="p-4">
                <div className="text-xs uppercase text-slate-500 mb-2">Top locations</div>
                <table className="w-full text-xs">
                  <thead><tr className="text-slate-500"><th className="text-left">Location</th><th>Signups</th><th>Revenue</th><th>Last staffed</th></tr></thead>
                  <tbody>{(loc?.top || []).map(r => <tr key={r.locationId} className="border-t"><td className="py-1">{r.locationName}</td><td className="text-center">{r.signups}</td><td className="text-right">{fmtGBP(r.revenue)}</td><td className="text-right">{r.lastStaffed || "—"}</td></tr>)}</tbody>
                </table>
                {(loc?.top || []).length === 0 && <div className="text-xs text-slate-400">No data</div>}
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="text-xs uppercase text-slate-500 mb-2">Underperforming locations</div>
                <table className="w-full text-xs">
                  <tbody>{(loc?.bottom || []).map(r => <tr key={r.locationId} className="border-t"><td className="py-1">{r.locationName}</td><td className="text-right">{r.signups}</td></tr>)}</tbody>
                </table>
                {(loc?.bottom || []).length === 0 && <div className="text-xs text-slate-400">None</div>}
              </CardContent></Card>
            </div>

            {/* Fundraisers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ci-fundraisers">
              <Card><CardContent className="p-4">
                <div className="text-xs uppercase text-slate-500 mb-2">Top fundraisers</div>
                <table className="w-full text-xs">
                  <thead><tr className="text-slate-500"><th className="text-left">Name</th><th>Signups</th><th>Canc.</th><th>Rev.</th><th>Shifts</th></tr></thead>
                  <tbody>{(fr?.top || []).map(r => <tr key={r.fundraiserId} className="border-t"><td className="py-1">{r.fundraiserName}</td><td className="text-center">{r.signups}</td><td className="text-center">{r.cancellations}</td><td className="text-right">{fmtGBP(r.revenue)}</td><td className="text-center">{r.shifts}</td></tr>)}</tbody>
                </table>
                {(fr?.top || []).length === 0 && <div className="text-xs text-slate-400">No data</div>}
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="text-xs uppercase text-slate-500 mb-2">Underperforming fundraisers</div>
                <table className="w-full text-xs">
                  <tbody>{(fr?.bottom || []).map(r => <tr key={r.fundraiserId} className="border-t"><td className="py-1">{r.fundraiserName}</td><td className="text-right">{r.signups}</td></tr>)}</tbody>
                </table>
                {(fr?.bottom || []).length === 0 && <div className="text-xs text-slate-400">None</div>}
              </CardContent></Card>
            </div>

            {/* ROI */}
            <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ci-roi">
              <Stat label="Payroll cost (est.)" value={fmtGBP(roi?.payrollCost)} />
              <Stat label="Revenue (est.)" value={fmtGBP(roi?.estRevenue)} />
              <Stat label="Net score" value={fmtGBP(roi?.netScore)} />
              <Stat label="Cost / signup" value={fmtGBP(roi?.costPerSignup)} />
            </CardContent></Card>

            {/* Quality */}
            <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ci-quality">
              <Stat label="Flagged signups" value={q?.flaggedSignups ?? 0} />
              <Stat label="Cancellation %" value={`${q?.cancellationPct ?? 0}%`} />
              <Stat label="Inactive locations" value={(q?.inactiveLocations || []).length} />
              <Stat label="Weak locations" value={(q?.underperformingLocations || []).length} />
            </CardContent></Card>

            {/* Actions */}
            <div className="flex flex-wrap gap-2" data-testid="ci-actions">
              <Button size="sm" variant="outline" onClick={() => onAction?.("open-planner", campaignId)}><Copy className="h-3.5 w-3.5 mr-1" />Open planner</Button>
              <Button size="sm" variant="outline" onClick={() => onAction?.("duplicate-week", campaignId)}>Duplicate successful week</Button>
              <Button size="sm" variant="outline" onClick={exportCSV} data-testid="ci-export-csv"><Download className="h-3.5 w-3.5 mr-1" />Export campaign data</Button>
              <Button size="sm" variant="outline" onClick={() => onAction?.("assign-staff", campaignId)}>Assign more staff</Button>
              <Button size="sm" variant="outline" onClick={() => onAction?.("add-locations", campaignId)}>Add locations</Button>
              <Button size="sm" variant="outline" onClick={() => onAction?.("edit-campaign", campaignId)}>Edit campaign</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
